import { Injectable } from '@angular/core';
import { DatabaseService } from './database.service';
import { MediaFile, Media, MediaFileType, MotesPaths, ContainerType, MediaLinkMap, MediaContainerLinkMap, DatabaseModel, MediaLink, MediaLinkJson, MediaLinkMapJson, Mote, ContainerOptions } from '../models';
import { UploadService } from './upload.service';
import { map, first } from 'rxjs/operators';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { MotesLoginService } from '@motes-sdk/motes-login';
import { HttpClient } from '@angular/common/http';
import { CopyMediaBody, CopyMediaResponse } from '@motes-sdk/api-models/media';

@Injectable({ providedIn: 'root' })
export class MediaService {

    private mediaLinkSub: Subscription = null;

    constructor(private http: HttpClient, private db: DatabaseService, private uploadService: UploadService, private loginService: MotesLoginService) {
    }

    public generateKey(): string {
        return this.db.database.ref().push().key;
    }

    public async updateMedia(media: Media, containerOptions: ContainerOptions, updateLinkedMotes: boolean = true): Promise<void> {
        const userId = this.loginService.userRights.uid;
        if(!media.createdOn) media.createdOn = this.db.serverTime();
        if(!media.createdBy) media.createdBy = userId;

        switch(containerOptions.type) {
            case (ContainerType.MOTE): {
                return this.setMoteMedia(media, containerOptions.containerKey, updateLinkedMotes);
            }
            case (ContainerType.PROFILE): {
                return this.setProfileMedia(media, userId);
            }
            case (ContainerType.WORLD): {
                return this.setWorldMedia(media);
            }
            case (ContainerType.PRODUCT): {
                return this.setProductMedia(media, containerOptions.containerKey, containerOptions.mediaIdx);
            }
            case (ContainerType.FEATURED_TOURS): {
                throw new Error('NOT YET IMPLEMENTED');
            }
            case (ContainerType.BLOG_POST): {
                return this.setBlogPostMedia(media, containerOptions.containerKey);
            }
            default: {
                throw new Error('Unimplemented containterType')
            }
        }
    }

    private async setMoteMedia(media: Media, storyKey: string, updateLinkedMotes: boolean = true): Promise<void> {
        if(!media.project) return console.error("Won't save media without a project", media);
        // TODO: Delete this when all legacy media has been deleted / converted (only very few motes/media should have this by now)
        const hasLegacyMediaKey = media.key == '0' || media.key == '1' || media.key == '2' || media.key == '3';
        if(!media.key || hasLegacyMediaKey) {
            console.warn("Detected legacy media without key - creating new key and deleting old...", media.key);
            // Remove legacy mote key
            if(hasLegacyMediaKey) await this.db.database.object(MotesPaths.MOTE_MEDIA_DB_PATH(storyKey, media.key)).remove();
            // Create new key
            media.key = DatabaseModel.genKey();
        }

        // Check if media links exist or create it
        const mediaLinks = await this.getMediaLink(media.key);
        if(storyKey && !mediaLinks[storyKey]) {
            await this.updateMediaLink(media.key, storyKey, ContainerType.MOTE);
        }
        // Update media
        await this.db.database.object(MotesPaths.MEDIA_LIB_DB_PATH(media.key)).set(media.toJson());
        // Update references in all motes containing this media (if any)
        if(updateLinkedMotes) await this.updateLinkedMedia(media);
    }

    private async setBlogPostMedia(media: Media, postKey: string): Promise<void> {
        await this.db.database.object(MotesPaths.POST_MEDIA_DB_PATH(postKey)).set(media.toJson());
    }

    private async setProfileMedia(media: Media, userId: string): Promise<void> {
        await this.db.database.object(MotesPaths.USER_PUBLIC_MEDIA_DB_PATH(userId)).set(media.toJson());
    }

    private async setWorldMedia(media: Media): Promise<void> {
        if(!media.project) return console.error("Won't save media without a project", media);

        // Check if world media links exist or create it
        const mediaLinks = await this.getMediaLink(media.key);
        if(!mediaLinks[media.project] || mediaLinks[media.project].type != ContainerType.WORLD) {
            await this.updateMediaLink(media.key, media.project, ContainerType.WORLD);
        }

        await this.db.database.object(MotesPaths.WORLD_MEDIA_DB_PATH(media.project)).set(media.toJson());
    }

    private async setProductMedia(media: Media, productKey: string, mediaIdx: number): Promise<void> {
        if(!media.project) return console.error("Won't save media without a project", media);

        // Check if product media links exist or create it
        const mediaLinks = await this.getMediaLink(media.key);
        if(!mediaLinks[media.project] || mediaLinks[media.project].type != ContainerType.PRODUCT) {
            await this.updateMediaLink(media.key, productKey, ContainerType.PRODUCT);
        }

        await this.db.database.object(MotesPaths.PRODUCT_MEDIA_DB_PATH(productKey, mediaIdx)).set(media.toJson());
    }

    public async updateMediaMetaData(media: Media): Promise<void> {
        const metaDataBody = {
            description: media.description,
            copyright: media.copyright,
        }

        await this.db.database.object(MotesPaths.MEDIA_LIB_DB_PATH(media.key)).update(metaDataBody);
        await this.updateLinkedMedia(media)
    }

    private async updateLinkedMedia(media: Media): Promise<void> {
        // Get all motes linking to media
        let linkMap = await this.getMediaLink(media.key);
        let moteKeys = Object.values(linkMap).map( link => link.containerKey );

        for(let moteKey of moteKeys) {
            let mediaPath = MotesPaths.MOTE_MEDIA_DB_PATH(moteKey, media.key);
            await this.db.object(mediaPath).set(media.toJson());
        }
    }

    /**
     * Delete a media element from all the motes that has references/links to it,
     * delete from the media library and delete the mediaLinks from the database as well
     * @deprecated
     */
    public async deleteMoteMedia(media: Media) {
        // Get all motes linking to media
        let linkMap = await this.getMediaLink(media.key);
        let moteKeys = Object.keys(linkMap);

        for(let moteKey of moteKeys) {
            // Delete link
            await this.deleteMediaLink(media.key, moteKey);
            // Delete media from mote
            let mediaPath = MotesPaths.MOTE_MEDIA_DB_PATH(moteKey, media.key);
            await this.db.object(mediaPath).remove();
        }
        // Delete media from media library including all files
        await this.deleteMedia(media);
    }

    /**
     * Delete a media database object and all the files it refers to
     * OBS: This will not delete mediaLinks and references in Motes, so this
     * should be done separately
     * @deprecated
     */
    public async deleteMedia(media: Media): Promise<void> {
        await this.deleteFiles(media.files);
        return this.db.database.object(MotesPaths.MEDIA_LIB_DB_PATH(media.key)).remove();
    }

    public async saveMoteMediaOrder(mote: Mote, media: Media) {
        let mediaPath = MotesPaths.MOTE_MEDIA_DB_PATH(mote.key, media.key);
        return this.db.database.object(`${mediaPath}/order`).set(media.order);
    }

    /**
     * Copy all files associated with the media and create a new Media object in database
     */
    public async copyMedia(mediaKey: string, containerOptions: ContainerOptions, copyMediaKey: string ): Promise<Media | null> {
        const authOptions = await this.loginService.getAuthOptions();

        const body: CopyMediaBody = {
            newMediaKey: copyMediaKey,
            containerOptions: containerOptions,
        }
        const url = MotesPaths.MOTES_REST + "/v1/media/" + mediaKey + "/copy";
        const rsp = await firstValueFrom(this.http.post<CopyMediaResponse>(url, body, { headers: authOptions.headers })).catch(err => {
            console.warn("Could not copy media", err);
            return null;
        });

        return (rsp) ? new Media(rsp.mediaJson) : null;
    }

    public watchMedia(mediaKey: string): Observable<Media> {
        return this.db.database.object(MotesPaths.MEDIA_LIB_DB_PATH(mediaKey)).snapshotChanges().pipe(map(snap => {
            return new Media(snap.payload.val(), snap.key);
        }));
    }

    public getMedia(mediaKey: string): Promise<Media> {
        return this.watchMedia(mediaKey).pipe(first()).toPromise().catch(() => null);
    }

    public watchProjectMedia(projectKey: string): Observable<Media[]> {
        // Select media from project
        let query = (ref) => ref.orderByChild('project').equalTo(projectKey);

        return this.db.database.list(MotesPaths.MEDIA_LIB_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            return snapList.map((snap) => new Media(snap.payload.val(), snap.key));
        }));
    }

    public watchUserMedia(userKey: string): Observable<Media[]> {
        // Select media from project
        let query = (ref) => ref.orderByChild('createdBy').equalTo(userKey);

        return this.db.database.list(MotesPaths.MEDIA_LIB_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            return snapList.map((snap) => new Media(snap.payload.val(), snap.key));
        }));
    }

    public watchMediaLinks(): Observable<MediaLinkMap> {
        return this.db.database.object<MediaLinkMapJson>(MotesPaths.MEDIA_LINK_DB_PATH()).snapshotChanges().pipe(map(snap => {
            // Get payload json from firebase
            const json = snap.payload.val() || {};
            // Convert entries of json map to Model instances of MediaContainerLinkMap
            const mediaLinkList = Object.entries(json).map(([mediaKey, linkJson]) => {
                // Return entry with media key as map hash
                return [mediaKey, MediaLink.getMediaContainerLinkMap(linkJson, mediaKey)];
            });
            return Object.fromEntries(mediaLinkList);
        }));
    }

    public watchMediaLink(mediaKey: string): Observable<MediaContainerLinkMap> {
        const mediaPath = MotesPaths.MEDIA_LINK_DB_PATH(mediaKey);
        return this.db.database.object<{ [mediaKey: string]: MediaLinkJson }>(mediaPath).valueChanges().pipe( map( json => {
            return MediaLink.getMediaContainerLinkMap(json);
        }));
    }

    public async deleteFiles(files: MediaFile[], deleteOrigin: boolean = true): Promise<void> {
        // Delete all storage paths
        for(let file of files) {
            if(!file.url || !file.storage) continue;

            // Skip delete of original url if requested (to prevent view from temporary updating)
            let skipOrigin = (!deleteOrigin && file.type == MediaFileType.ORIGINAL);
            if(skipOrigin) continue;

            await this.uploadService.deleteFile(file).catch( (err) => console.log("Could not delte old file", err));
            // Delete this storage path from media
            file.url = "";
            file.blob = null;
        }
    }

    public async updateMediaLink(mediaKey: string, containerKey: string, containerType: ContainerType) {
        const mediaLink = new MediaLink({ type: containerType, containerKey: containerKey });
        await this.db.database.object(MotesPaths.MEDIA_LINK_DB_PATH(mediaKey) + "/" + containerKey).set(mediaLink.toJson());
    }

    public async deleteMediaLink(mediaKey: string, moteKey: string) {
        await this.db.database.object(MotesPaths.MEDIA_LINK_DB_PATH(mediaKey) + "/" + moteKey).remove();
    }

    /**
     * Get media links to motes
     * @return List of motes (keys) that have reference to the given media (key)
     */
    public async getMediaLink(mediaKey: string): Promise<MediaContainerLinkMap> {
        const json = await this.watchMediaLink(mediaKey).pipe(first()).toPromise();
        return MediaLink.getMediaContainerLinkMap(json);
    }

    ngOnDestroy() {
        // console.log('Media service destroyed successfully');
        if(this.mediaLinkSub) this.mediaLinkSub.unsubscribe();
    }
}
